/**
 * App.tsx — Phase 2
 * ------------------------------------------------------------------
 * Auth gate + DataProvider + Shell routing. Setup, dashboard and
 * term management are live. Marks / comments / flags / reports /
 * remaining admin screens are stubbed with clear "coming in Phase N"
 * panels so navigation is complete and testable now.
 * ------------------------------------------------------------------
 */
import { useState, useEffect, lazy, Suspense, type ReactNode } from 'react';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { canOpenRoute, fallbackRoute } from '@/lib/routeAccess';
import { DataProvider } from '@/contexts/DataContext';
import { DirtyProvider } from '@/contexts/DirtyContext';
import VersionGate from '@/components/VersionGate';
import { TermKey, currentTermKey } from '@/lib/school.config';
import { Spinner } from '@/components/ui/primitives';
import Shell, { NAV, type TabId } from '@/components/Shell';
import Login from '@/features/auth/Login';
import AuthAction from '@/features/auth/AuthAction';
import SplashScreen from '@/components/SplashScreen';
import Dashboard from '@/features/dashboard/Dashboard';
import HeadInsightDetail, { type HeadFocus } from '@/features/dashboard/HeadInsightDetail';
import ParentDashboard from '@/features/parent/ParentDashboard';
import ParentMessagesHub from '@/features/communications/ParentMessagesHub';
import SchoolCommunications from '@/features/communications/SchoolCommunications';
import ParentNoticesManager from '@/features/communications/ParentNoticesManager';
import ParentContacts from '@/features/communications/ParentContacts';
import TeacherParentComms from '@/features/communications/TeacherParentComms';
import SubjectsSetup from '@/features/setup/SubjectsSetup';
import ClassesSetup from '@/features/setup/ClassesSetup';
import TeachersSetup from '@/features/setup/TeachersSetup';
import StudentsSetup from '@/features/setup/StudentsSetup';
import AlumniView from '@/features/students/AlumniView';
import NakoLaunch from '@/features/timetable/NakoLaunch';
import PhotoManagement from '@/features/students/PhotoManagement';
import TeacherSwap from '@/features/setup/TeacherSwap';
import FamiliesManager from '@/features/admin/FamiliesManager';
import StudentProfile from '@/features/students/StudentProfile';
import TermsList from '@/features/terms/TermsList';
import TermDetail from '@/features/terms/TermDetail';
import UserManagement from '@/features/admin/UserManagement';
import DataAudit from '@/features/admin/DataAudit';
import ActivityLog from '@/features/admin/ActivityLog';
import BulkImport from '@/features/admin/BulkImport';
import CommentTemplatesManager from '@/features/admin/CommentTemplatesManager';
import UserSettings from '@/features/account/UserSettings';
import BulkCommentImport from '@/features/admin/BulkCommentImport';
import SetupDashboard from '@/features/setup/SetupDashboard';
import ParentOnboarding from '@/features/setup/ParentOnboarding';
import MarkEntry from '@/features/marks/MarkEntry';
import AttendanceModule from '@/features/attendance/AttendanceModule';
import ClassTeacherComment from '@/features/comments/ClassTeacherComment';
import HodComment from '@/features/comments/HodComment';
import CommentFlags from '@/features/comments/CommentFlags';
import CommentReview from '@/features/comments/CommentReview';
import TeacherDepartmentAnalysis from '@/features/reports/TeacherDepartmentAnalysis';
import ReportGeneration from '@/features/reports/ReportGeneration';
import GeneratedReports from '@/features/reports/GeneratedReports';
import BulkSend from '@/features/reports/BulkSend';
import AdmissionsModule from '@/features/admissions/AdmissionsModule';
import DisciplineModule from '@/features/discipline/DisciplineModule';
import AchievementsModule from '@/features/achievements/AchievementsModule';
import PromotionWizard from '@/features/admin/PromotionWizard';
import TeacherProfile from '@/features/teachers/TeacherProfile';
import CompositeMarksheet from '@/features/reports/CompositeMarksheet';
import ExamsHub from '@/features/exams/ExamsHub';
import MarksCompliance from '@/features/reports/MarksCompliance';
import ParentPortal from '@/features/parent/ParentPortal';
import ParentFinance from '@/features/parent/ParentFinance';
import ParentSettings from '@/features/admin/ParentSettings';
import TermDatesSettings from '@/features/admin/TermDatesSettings';
import TeachingDaysSettings from '@/features/admin/TeachingDaysSettings';
import AnnouncementsManager from '@/features/admin/AnnouncementsManager';
/* Teaching + study portal, ported from Al-Nur (TEACHING-AND-STUDENT-PORTAL-PLAN.md).
   LAZY, unlike the rest of this file: they carry the shared lessons,
   syllabuses and question-bank code (several MB), which would otherwise land
   in the one bundle every parent and bursar downloads — and push it past the
   service worker's precache limit. Each loads when its screen is opened. */
const TeachingLessons = lazy(() => import('@/features/lessons/TeachingLessons'));
const HomeworkHub = lazy(() => import('@/features/homework/HomeworkHub'));
const SchemeHub = lazy(() => import('@/features/curriculum/SchemeHub'));
const LessonPlans = lazy(() => import('@/features/curriculum/LessonPlans'));
const Markbook = lazy(() => import('@/features/markbook/Markbook'));
const TextbooksScreen = lazy(() => import('@/features/textbooks/TextbooksScreen'));
const ExamRoomScreen = lazy(() => import('@/features/exams/ExamRoomScreen'));
const MyTimetable = lazy(() => import('@/features/timetable/MyTimetable'));
const LessonRegister = lazy(() => import('@/features/timetable/LessonRegister'));
const CoverScreen = lazy(() => import('@/features/cover/CoverScreen'));
const StaffingScreen = lazy(() => import('@/features/timetable/StaffingScreen'));
const SubjectChoicesAdmin = lazy(() => import('@/features/curriculum/SubjectChoicesAdmin'));
const StudyHub = lazy(() => import('@/features/study/StudyHub'));
const Marking = lazy(() => import('@/features/study/Marking'));
const StudyLibrary = lazy(() => import('@/features/study/StudyLibrary'));
const Practicals = lazy(() => import('@/features/study/Practicals'));
const SyllabusEditor = lazy(() => import('@/features/study/SyllabusEditor'));
const StudentPortal = lazy(() => import('@/features/student/StudentPortal'));
/** What a lazy screen shows while its code arrives. */
function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<div className="py-16"><Spinner label="Loading…" /></div>}>{children}</Suspense>;
}
import PublicApplicationForm from '@/features/applications/PublicApplicationForm';
import StaffDirectory from '@/features/hr/StaffDirectory';
import ComplianceCenter from '@/features/hr/ComplianceCenter';
import ClockingBoard from '@/features/hr/ClockingBoard';
import ClockingImport from '@/features/hr/ClockingImport';
import AgeAnalysisCheck from '@/features/finance/AgeAnalysisCheck';
import LeaveCenter from '@/features/hr/LeaveCenter';
import PublicHolidays from '@/features/hr/PublicHolidays';
import ContractsCenter from '@/features/hr/ContractsCenter';
import AppraisalsCenter from '@/features/hr/AppraisalsCenter';
import PayrollHub from '@/features/hr/PayrollHub';
import FinanceOverview from '@/features/finance/FinanceOverview';
import FeeSetup from '@/features/finance/FeeSetup';
import FeesSpecialCases from '@/features/finance/FeesSpecialCases';
import Debtors from '@/features/finance/Debtors';
import FeeArrangements from '@/features/finance/FeeArrangements';
import Adjustments from '@/features/finance/Adjustments';
import SiblingDiscounts from '@/features/finance/SiblingDiscounts';
import Inbox from '@/features/communications/Inbox';
import ChartOfAccounts from '@/features/finance/ChartOfAccounts';
import Journals from '@/features/finance/Journals';
import GeneralLedger from '@/features/finance/GeneralLedger';
import OpeningBalances from '@/features/finance/OpeningBalances';
import PeriodEnd from '@/features/finance/PeriodEnd';
import LedgerReports from '@/features/finance/LedgerReports';
import CashFlow from '@/features/finance/CashFlow';
import ControlAccounts from '@/features/finance/ControlAccounts';
import Anomalies from '@/features/finance/Anomalies';
import SuppliersScreen from '@/features/finance/Suppliers';
import VouchersScreen from '@/features/finance/Vouchers';
import RequisitionsScreen from '@/features/finance/RequisitionsHub';
import PurchaseOrdersScreen from '@/features/finance/PurchaseOrders';
import PaymentBatchesScreen from '@/features/finance/PaymentBatches';
import FeeStatusScreen from '@/features/finance/FeeStatusOverview';
import AnalysisScreen from '@/features/finance/Analysis';
import AssetRegisterScreen from '@/features/finance/AssetRegister';
import BudgetsScreen from '@/features/finance/Budgets';
import HousekeepingScreen from '@/features/finance/Housekeeping';
import FinancialYearScreen from '@/features/finance/FinancialYear';
import AuditPackScreen from '@/features/finance/AuditPack';
import TaxVatScreen from '@/features/finance/TaxVat';
import DetailedLedgerScreen from '@/features/finance/DetailedLedger';
import PartyJournalsScreen from '@/features/finance/PartyJournals';
import DocumentsScreen from '@/features/finance/Documents';
import BankProofScreen from '@/features/finance/BankProofPanel';
import CashBookScreen from '@/features/finance/CashBook';
import StandingBillsScreen from '@/features/finance/RecurringVouchers';
import PettyCashScreen from '@/features/finance/PettyCash';
import RemittanceScreen from '@/features/finance/Remittance';
import SupplierAgingScreen from '@/features/finance/SupplierAging';
import SupplierStatementsScreen from '@/features/finance/SupplierStatements';
import BoardersPortal from '@/features/boarding/BoardersPortal';
import BillingRun from '@/features/finance/BillingRun';
import { ManualInvoiceScreen } from '@/features/finance/ManualInvoice';
import { ExamFeesScreen } from '@/features/finance/ExamFeesScreen';
import Payments from '@/features/finance/Payments';
import UnidentifiedPayments from '@/features/finance/UnidentifiedPayments';
import CashUp from '@/features/finance/CashUp';
import Statements from '@/features/finance/Statements';
import Arrears from '@/features/finance/Arrears';
import BankReconciliation from '@/features/finance/BankReconciliation';
import Severance from '@/features/hr/Severance';
import LoansHub from '@/features/hr/LoansHub';
import MedicalAid from '@/features/hr/MedicalAid';
import PaymentDetails from '@/features/hr/PaymentDetails';
import TaxTablesAdmin from '@/features/hr/TaxTablesAdmin';
import StaffProfile from '@/features/hr/StaffProfile';
import BursHub from '@/features/burs/BursHub';
import MyPayHr from '@/features/selfservice/MyPayHr';


function hashTab(): { tab: TabId; studentId?: string } {
  const h = window.location.hash.replace(/^#\/?/, '');
  if (!h) return { tab: 'home' };
  const [tab, id] = h.split('/');
  // Deep-link routes that carry an entity ID
  if (tab === 'studentProfile' && id) {
    return { tab: 'studentProfile', studentId: id };
  }
  if (tab === 'teacherProfile' && id) {
    return { tab: 'teacherProfile', studentId: id };
  }
  if (tab === 'staffProfile' && id) {
    return { tab: 'staffProfile', studentId: id };
  }
  // #/examRegisters/<registerId> opens one paper's register, from a duty.
  if (tab === 'examRegisters' && id) {
    return { tab: 'examRegisters', studentId: id };
  }
  if (tab === 'admissions' && id) {
    return { tab: 'admissions', studentId: id };
  }
  // Round 7: #/terms/<TermKey> deep-link uses studentId slot to carry the term key
  if (tab === 'terms' && id) {
    return { tab: 'terms', studentId: id };
  }
  // Head dashboard drill-down: #/headInsight/<focus> carries the focus key
  if (tab === 'headInsight' && id) {
    return { tab: 'headInsight', studentId: id };
  }
  // Round 7: legacy #/assessmentConfig redirects to unified Terms list
  if (tab === 'assessmentConfig') {
    return { tab: 'terms' };
  }
  return { tab: tab as TabId };
}

/**
 * What a refused route shows.
 *
 * Named and explained rather than a blank page or a silent redirect: a
 * person who followed a link they should not have needs to know the screen
 * exists and is not theirs, not to wonder whether the app is broken.
 */
function NotPermitted({ onHome, label }: { onHome: () => void; label?: string }) {
  return (
    <div className="max-w-lg mx-auto text-center py-20 space-y-3">
      <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
        {label ?? 'That screen is not available to you.'}
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        If you think you should have access to it, ask the school office to check your role.
      </p>
      <button
        type="button"
        onClick={onHome}
        className="mt-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-primary hover:bg-primaryLight"
      >
        Take me back
      </button>
    </div>
  );
}

function AppContent() {
  const initial = hashTab();
  const { userData, permissions } = useAuth();
  const [active, setActive] = useState<TabId>(initial.tab);
  const [studentId, setStudentId] = useState<string | undefined>(
    initial.studentId,
  );
  const [year, setYear] = useState(String(new Date().getFullYear()));
  // Default to the term we're actually in (e.g. Term 2 mid-year); Term 1
  // remains selectable from the term picker.
  const [term, setTerm] = useState<TermKey>(currentTermKey());

  // Admissions-only users land directly on the Admissions page
  useEffect(() => {
    if (userData?.role === 'admissions' && active === 'home') {
      setActive('admissions');
      window.history.replaceState(null, '', '#/admissions');
    }
    // Self-service staff (no system access) land on their Pay & HR view.
    if (userData?.role === 'staff' && userData?.linkedStaffId && active === 'home') {
      setActive('myPayHr');
      window.history.replaceState(null, '', '#/myPayHr');
    }
    // Parents now land on a proper ParentDashboard at `home` (rendered
    // in the case 'home' branch below). No forced redirect.
  }, [userData?.role, userData?.linkedStaffId, active]);

  useEffect(() => {
    const onHash = () => {
      const { tab, studentId } = hashTab();
      setActive(tab);
      setStudentId(studentId);
    };
    window.addEventListener('popstate', onHash);
    window.addEventListener('hashchange', onHash);
    return () => {
      window.removeEventListener('popstate', onHash);
      window.removeEventListener('hashchange', onHash);
    };
  }, []);

  function navigate(t: TabId, opts?: { studentId?: string }) {
    setActive(t);
    setStudentId(opts?.studentId);
    let newHash = t === 'home' ? '#/' : `#/${t}`;
    // Deep-link routes that carry an entity ID in the URL
    const entityRoutes: TabId[] = ['studentProfile', 'teacherProfile', 'admissions', 'terms', 'staffProfile', 'headInsight'];
    if (entityRoutes.includes(t) && opts?.studentId) {
      newHash = `#/${t}/${opts.studentId}`;
    }
    if (window.location.hash !== newHash) {
      window.history.pushState({ t, studentId: opts?.studentId }, '', newHash);
    }
  }

  function render() {
    switch (active) {
      case 'home':
        return userData?.role === 'parent' ? (
          <ParentDashboard onNavigate={navigate} />
        ) : (
          <Dashboard onNavigate={navigate} year={year} term={term} />
        );
      case 'subjects':
        return <SubjectsSetup />;
      case 'classes':
        return <ClassesSetup />;
      case 'teachers':
        return <TeachersSetup />;
      case 'students':
        return <StudentsSetup />;
      case 'alumni':
        return <AlumniView />;
      case 'teacherSwap':
        return <TeacherSwap />;
      case 'families':
        return <FamiliesManager />;
      case 'admissions':
        return <AdmissionsModule editStudentId={studentId} />;
      case 'discipline':
        return <DisciplineModule />;
      case 'achievements':
        return <AchievementsModule />;
      case 'terms':
        // Round 7: list view if no term in URL, detail view if #/terms/<TermKey>
        if (studentId) {
          return (
            <TermDetail
              year={year}
              term={studentId as TermKey}
              onBack={() => navigate('terms')}
            />
          );
        }
        return <TermsList year={year} />;
      case 'promotion':
        return <PromotionWizard year={year} />;
      case 'parentPortal':
        return <ParentPortal />;
      case 'parentFees':
        return <ParentFinance />;
      case 'parentMessages':
        return <ParentMessagesHub />;
      case 'schoolComms':
        return <SchoolCommunications />;
      case 'parentSettings':
        return <ParentSettings />;
      case 'termDates':
        return <TermDatesSettings year={year} />;
      case 'teachingDays':
        return <TeachingDaysSettings />;
      case 'myTimetable':
        return <Lazy><MyTimetable /></Lazy>;
      case 'lessonRegister':
        return <Lazy><LessonRegister /></Lazy>;
      case 'cover':
        return <Lazy><CoverScreen /></Lazy>;
      case 'nako':
        return <NakoLaunch />;
      case 'staffing':
        return <Lazy><StaffingScreen /></Lazy>;
      case 'subjectChoices':
        return <Lazy><SubjectChoicesAdmin /></Lazy>;
      case 'announcements':
        return <AnnouncementsManager />;
      case 'photoManagement':
        return <PhotoManagement />;
      case 'schoolMailbox':
        return <Inbox />;
      case 'parentContacts':
        return <ParentContacts />;
      case 'parentNotices':
        return <ParentNoticesManager />;
      case 'messageParents':
        return <TeacherParentComms />;
      case 'teachingLessons':
        return <Lazy><TeachingLessons /></Lazy>;
      case 'homework':
        return <Lazy><HomeworkHub key={studentId ?? ''} initialPane={studentId} /></Lazy>;
      case 'schemes':
        return <Lazy><SchemeHub year={year} term={term} /></Lazy>;
      case 'lessonPlans':
        return <Lazy><LessonPlans year={year} term={term} /></Lazy>;
      case 'markbook':
        return <Lazy><Markbook year={year} /></Lazy>;
      case 'textbooks':
        return <Lazy><TextbooksScreen year={year} term={term} /></Lazy>;
      case 'textbookCharges':
        return <Lazy><TextbooksScreen mode="charges" year={year} term={term} /></Lazy>;
      case 'textbookClearance':
        return <Lazy><TextbooksScreen mode="clearance" year={year} term={term} /></Lazy>;
      case 'studyProgress':
        return <Lazy><StudyHub /></Lazy>;
      case 'marking':
        return <Lazy><Marking /></Lazy>;
      case 'studyLibrary':
        return <Lazy><StudyLibrary /></Lazy>;
      case 'practicals':
        return <Lazy><Practicals /></Lazy>;
      case 'syllabusEditor':
        return <Lazy><SyllabusEditor /></Lazy>;
      case 'studyPortal':
        /* The pupil's own portal. No nav entry: a student-only account is
           sent here directly, and no such account exists until the student
           role and the rules sweep land (plan, stage S0). */
        return <Lazy><StudentPortal /></Lazy>;
      case 'hrStaff':
        return <StaffDirectory onOpenStaff={(id) => navigate('staffProfile', { studentId: id })} />;
      case 'hrCompliance':
        return <ComplianceCenter onOpenStaff={(id) => navigate('staffProfile', { studentId: id })} />;
      case 'hrClocking':
        return (
          <ClockingBoard
            onOpenStaff={permissions.canAccessHR ? (id) => navigate('staffProfile', { studentId: id }) : undefined}
            onImport={permissions.canImportClocking ? () => navigate('hrClockingImport') : undefined}
          />
        );
      case 'hrClockingImport':
        return <ClockingImport onBack={permissions.canViewClocking ? () => navigate('hrClocking') : undefined} />;
      case 'hrLeave':
        return <LeaveCenter onOpenStaff={(id) => navigate('staffProfile', { studentId: id })} />;
      case 'hrContracts':
        return <ContractsCenter onOpenStaff={(id) => navigate('staffProfile', { studentId: id })} />;
      case 'hrAppraisals':
        return <AppraisalsCenter onOpenStaff={(id) => navigate('staffProfile', { studentId: id })} />;
      case 'hrPayroll':
        return <PayrollHub onOpenStaff={(id) => navigate('staffProfile', { studentId: id })} />;
      case 'hrSeverance':
        return <Severance />;
      case 'hrLoans':
        return <LoansHub onOpenStaff={(id) => navigate('staffProfile', { studentId: id })} />;
      case 'hrMedicalAid':
        return <MedicalAid onOpenStaff={(id) => navigate('staffProfile', { studentId: id })} />;
      case 'hrPaymentDetails':
        return <PaymentDetails onOpenStaff={(id) => navigate('staffProfile', { studentId: id })} />;
      case 'financeOverview':
        return <FinanceOverview onNavigate={navigate} />;
      case 'financeFees':
        return <FeeSetup />;
      case 'financeSpecialCases':
        return <FeesSpecialCases />;
      case 'financeDebtors':
        return <Debtors onNavigate={navigate} />;
      case 'financeArrangements':
        return <FeeArrangements />;
      case 'financeSiblingDiscounts':
        return <SiblingDiscounts />;
      case 'financeAdjustments':
        return <Adjustments />;
      case 'financeInvoice':
        return <ManualInvoiceScreen />;
      case 'financeExamFees':
        return <ExamFeesScreen />;
      case 'financeBilling':
        return <BillingRun />;
      case 'financePayments':
        return <Payments />;
      case 'financeUnidentified':
        return <UnidentifiedPayments />;
      case 'financeCashUp':
        return <CashUp />;
      case 'financeStatements':
        return <Statements />;
      case 'financeAgeAnalysis':
        return <AgeAnalysisCheck />;
      case 'financeArrears':
        return <Arrears />;
      case 'financeReconcile':
        return <BankReconciliation />;
      case 'financeJournals':
        return <Journals />;
      case 'financeGeneralLedger':
        return <GeneralLedger />;
      case 'financeOpeningBalances':
        return <OpeningBalances />;
      case 'financePeriodEnd':
        return <PeriodEnd />;
      /* One component, three views — the same figures cut three ways, so a
         reader moving between them cannot find the totals disagreeing. */
      case 'financeTrialBalance':
        return <LedgerReports view="trial" />;
      case 'financeIncomeExp':
        return <LedgerReports view="incomeExp" />;
      case 'financeBalanceSheet':
        return <LedgerReports view="balanceSheet" />;
      case 'financeCashFlow':
        return <CashFlow />;
      case 'financeControlAccounts':
        return <ControlAccounts />;
      case 'financeAnomalies':
        return <Anomalies />;
      case 'financeSuppliers':
        return <SuppliersScreen />;
      case 'financeVouchers':
        return <VouchersScreen />;
      case 'financeRequisitions':
        return <RequisitionsScreen />;
      case 'financePurchaseOrders':
        return <PurchaseOrdersScreen />;
      case 'financePaymentBatches':
        return <PaymentBatchesScreen />;
      case 'financeFeeStatus':
        return <FeeStatusScreen />;
      case 'financeAnalysis':
        return <AnalysisScreen />;
      case 'financeAssets':
        return <AssetRegisterScreen />;
      case 'financeBudgets':
        return <BudgetsScreen />;
      case 'financeHousekeeping':
        return <HousekeepingScreen />;
      case 'financeFinancialYear':
        return <FinancialYearScreen />;
      case 'financeAuditPack':
        return <AuditPackScreen />;
      case 'financeTaxVat':
        return <TaxVatScreen />;
      case 'financeDetailedLedger':
        return <DetailedLedgerScreen />;
      case 'financePartyJournals':
        return <PartyJournalsScreen />;
      case 'financeDocuments':
        return <DocumentsScreen />;
      case 'financeBankProof':
        return <BankProofScreen />;
      case 'financeCashBook':
        return <CashBookScreen />;
      case 'financeStandingBills':
        return <StandingBillsScreen />;
      case 'financePettyCash':
        return <PettyCashScreen />;
      case 'financeRemittance':
        return <RemittanceScreen />;
      case 'financeCreditorsAging':
        return <SupplierAgingScreen />;
      case 'financeSupplierStatements':
        return <SupplierStatementsScreen />;
      case 'financeChartOfAccounts':
        return <ChartOfAccounts />;
      case 'myBoarders':
        return <BoardersPortal />;
      case 'publicHolidays':
        return <PublicHolidays canEdit={permissions.canAccessHR} />;
      case 'hrTaxTables':
        return <TaxTablesAdmin />;
      case 'hrBurs':
        return <BursHub />;
      case 'myPayHr':
        return <MyPayHr />;
      case 'staffProfile':
        if (!studentId) {
          return <StaffDirectory onOpenStaff={(id) => navigate('staffProfile', { studentId: id })} />;
        }
        return (
          <StaffProfile
            staffId={studentId}
            onBack={() => navigate('hrStaff')}
          />
        );
      case 'assessmentConfig':
        // Round 7: legacy route - render the unified Terms list instead.
        // The hashTab parser also rewrites the URL, but this catches any
        // direct case-fallthrough from older state.
        return <TermsList year={year} />;

      case 'marks':
        return <MarkEntry year={year} term={term} />;
      case 'attendance':
        return <AttendanceModule />;
      case 'classComment':
        return <ClassTeacherComment year={year} term={term} />;
      case 'hodComment':
        return <HodComment year={year} term={term} />;
      case 'flags':
        return <CommentFlags year={year} term={term} />;
      case 'commentReview':
        return <CommentReview year={year} term={term} />;
      case 'progress':
      case 'headInsight':
        return (
          <HeadInsightDetail
            focus={(studentId as HeadFocus) || 'atRisk'}
            year={year}
            term={term}
            onNavigate={navigate}
          />
        );
      case 'marksheet':
        return <CompositeMarksheet year={year} term={term} />;
      case 'teacherAnalysis':
        return <TeacherDepartmentAnalysis year={year} term={term} />;
      case 'marksCompliance':
        return <MarksCompliance year={year} term={term} />;
      case 'exams':
        return <ExamsHub pane="days" />;
      case 'examTimetable':
        return <ExamsHub pane="timetable" />;
      case 'internalExams':
        return <ExamsHub pane="internal" />;
      case 'examRegistration':
        return <ExamsHub pane="registration" />;
      case 'examTimetables':
        return <ExamsHub pane="candidates" />;
      case 'examTools':
        return <ExamsHub pane="tools" year={year} term={term} />;
      case 'invigilation':
        return <ExamsHub pane="invigilation" />;
      case 'myInvigilation':
        return <ExamsHub pane="myDuties" />;
      case 'examRegisters':
        return <ExamsHub pane="registers" openId={studentId} />;
      case 'examSeating':
        return <ExamsHub pane="seating" />;
      case 'examRoom':
        return <Lazy><ExamRoomScreen /></Lazy>;
      case 'reports':
        return <ReportGeneration year={year} term={term} />;
      case 'viewReports':
        return <GeneratedReports year={year} term={term} />;
      case 'bulkSend':
        return <BulkSend year={year} term={term} />;
      case 'users':
        return <UserManagement />;
      case 'audit':
        return <DataAudit />;
      case 'activity':
        return <ActivityLog />;
      case 'bulkImport':
        return <BulkImport />;
      case 'templates':
        return <CommentTemplatesManager />;
      case 'account':
        return <UserSettings />;
      case 'bulkComments':
        return <BulkCommentImport year={year} term={term} />;
      case 'setupHub':
        return <SetupDashboard onNavigate={navigate} />;
      case 'parentOnboarding':
        return <ParentOnboarding />;
      case 'studentProfile':
        if (!studentId) {
          // A profile route with no pupil used to fall through to the whole
          // roster. It now goes wherever this user may actually go.
          return <NotPermitted onHome={() => navigate(fallbackRoute(['students', 'marks'], permissions))} label="No pupil selected." />;
        }
        return (
          <StudentProfile
            studentId={studentId}
            /* NOT a hardcoded 'students'. That sent every viewer to the
               whole-school roster, including the teachers who had arrived
               from their own class and had no business there. Back now goes
               to the first screen this user is actually allowed to be on. */
            onBack={() => navigate(fallbackRoute(['students', 'marks', 'viewReports'], permissions))}
          />
        );
      case 'teacherProfile':
        if (!studentId) {
          return <TeachersSetup />;
        }
        return (
          <TeacherProfile
            teacherId={studentId}
            onBack={() => navigate('teachers')}
            year={year}
            term={term}
          />
        );
      default:
        /*
         * A tab with a NAV ENTRY but no case above is a bug, not a default.
         *
         * This branch used to return the Dashboard for anything unmatched,
         * which meant a screen that was built, deployed and listed in the
         * sidebar could open the Dashboard and look like it had worked.
         * TypeScript cannot catch it — the default absorbs every unhandled
         * TabId — so the failure is made loud here instead. That matters now:
         * the accounting layer adds screens in batches, and a case missed in
         * the switch is the easiest thing in this file to miss.
         */
        return NAV.some((n) => n.id === active)
          ? <UnwiredScreen tab={active} />
          : <Dashboard onNavigate={navigate} year={year} term={term} />;
    }
  }

  const hrScoped = active.startsWith('hr') || active === 'staffProfile';
  const financeScoped = active.startsWith('finance');

  /* A STUDENT-only account gets the study portal and nothing else, whatever
     the hash says: no staff shell, no year/term pickers, no nav (from Al-Nur).
     The portal has its own rail. No such account exists until the student
     role becomes assignable (TEACHING-AND-STUDENT-PORTAL-PLAN.md, S0). */
  const appRoles: string[] = userData?.roles?.length
    ? (userData.roles as string[])
    : userData?.role ? [userData.role as string] : [];
  if (appRoles.length > 0 && appRoles.every((r) => r === 'student')) {
    return <Lazy><StudentPortal /></Lazy>;
  }

  return (
    <Shell
      active={active}
      onNavigate={navigate}
      year={year}
      term={term}
      onYearChange={setYear}
      onTermChange={setTerm}
    >
      <div className={hrScoped ? 'hr-scope' : financeScoped ? 'finance-scope' : undefined}>
        {/* THE GATE, ON RENDER — not only in the sidebar.
            Hiding a link is not an access control: the hash is editable, the
            back button replays history, and a hardcoded onBack can send
            someone where the sidebar never would. That is how a teacher
            pressing Back out of a pupil profile landed on the whole-school
            roster. Every route is now checked at the point it renders. */}
        {canOpenRoute(active, permissions) ? render() : <NotPermitted onHome={() => navigate('home')} />}
      </div>
    </Shell>
  );
}

function Gate() {
  // PUBLIC ROUTE: ?form=apply renders the application form with NO
  // auth, NO DataProvider — applicants are unauthenticated parents
  // arriving from the school website. The submitApplication Cloud
  // Function handles everything server-side.
  const isPublicApplyRoute =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('form') === 'apply';
  if (isPublicApplyRoute) {
    return <PublicApplicationForm />;
  }

  // PUBLIC ROUTE: Firebase email action links (account activation /
  // set-password) arrive as ?mode=…&oobCode=… with NO auth. Render the
  // in-app handler so the flow stays on lkcportal.com and the wording is
  // consistent end-to-end (framed as activation, not "password reset").
  const actionParams =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : null;
  if (actionParams?.get('mode') && actionParams?.get('oobCode')) {
    return <AuthAction />;
  }

  const { currentUser, userData, userRole, loading, profileLoading } =
    useAuth();

  // While auth is resolving, the SplashScreen overlay covers the screen
  // and fades out (after a minimum hold) once loading completes. The
  // content below renders beneath the overlay the whole time, so the
  // login form is already painted when the splash lifts.
  const authResolving = loading || profileLoading;

  let content: ReactNode;
  if (authResolving) {
    // Bare navy backdrop beneath the splash (prevents any flash if the
    // fade reveals this for a frame before login mounts).
    content = <div className="min-h-screen bg-primary" />;
  } else if (!currentUser) {
    content = <Login />;
  } else if (!userData || !userRole) {
    content = (
      <div className="min-h-screen flex items-center justify-center bg-primary">
        <Spinner label="Loading profile…" />
      </div>
    );
  } else {
    content = (
      <DataProvider>
        <DirtyProvider>
          <AppContent />
          {/* Version gate: single non-dismissible banner + safe auto-reload.
              Inside DataProvider (serverBuild) and DirtyProvider (unsaved-work). */}
          <VersionGate />
        </DirtyProvider>
      </DataProvider>
    );
  }

  return (
    <>
      {content}
      <SplashScreen ready={!authResolving} />
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Gate />
    </AuthProvider>
  );
}

/**
 * Shown when a sidebar entry resolves to no screen.
 *
 * Deliberately alarming, and deliberately naming the tab: the person who sees
 * this is either the developer who just added the nav entry, or a member of
 * staff who should be told plainly that the page is missing rather than being
 * silently handed the dashboard and left to think they misclicked.
 */
function UnwiredScreen({ tab }: { tab: string }) {
  return (
    <div className="max-w-2xl mx-auto mt-10 rounded-2xl border-2 border-red-400 bg-red-50 p-6 text-red-900 dark:border-red-500/60 dark:bg-red-900/30 dark:text-red-100">
      <h2 className="text-lg font-semibold">This screen is not wired up</h2>
      <p className="text-sm mt-2">
        The tab <code className="px-1 rounded bg-black/10 dark:bg-white/10">{tab}</code> appears in
        the sidebar but has no screen behind it. Nothing you did caused this —
        please report it.
      </p>
    </div>
  );
}
